import type { JSX } from 'react';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { EmbedPriority, type ParserRule, type VisitorContext } from './rules/types';

interface InserterProps {
  elements: string | JSX.Element | JSX.Element[];
  rules: ParserRule[];
}

interface ApplyRulesHelper extends Omit<InserterProps, 'elements'> {
  elements: JSX.Element | JSX.Element[];
  nodeTracker: Record<string, number>; // Optional, used for tracking the number of elements of each type
  startingIndex?: number; // Optional, used for tracking the index of the element in the parent
}

/** Normalize element(s) to an array */
function normalizeToArray(element: JSX.Element | JSX.Element[]): JSX.Element[] {
  if (element === null || element === undefined) {
    return [];
  }
  if (Array.isArray(element)) {
    return element;
  }
  return [element];
}

function isPaywallElement(element: JSX.Element): boolean {
  return (
    (element.props?.className?.includes('paywall') && !element.props?.className?.includes('paywall-selector')) ||
    element.key === 'paywall'
  );
}

function findPaywallIndex(elements: JSX.Element[]) {
  let paywallIndex = 0;
  let paywallElement: JSX.Element | null = null;
  for (paywallIndex; paywallIndex < elements.length; paywallIndex++) {
    const element = elements[paywallIndex];
    if (isPaywallElement(element)) {
      paywallElement = element;
      break;
    }
  }
  return { paywallElement, paywallIndex };
}

// Run through each element, then check each rule to see if it matches the element.
// If it does, insert the component either before or after the element.
const applyRulesHelper = ({ elements, rules, startingIndex = 0, nodeTracker }: ApplyRulesHelper) => {
  const elementArray = normalizeToArray(elements);
  const reactTree: React.ReactNode[] = [];
  elementArray.forEach((element, index) => {
    const tag = element.type?.toString();

    nodeTracker[tag] = (nodeTracker[tag] || 0) + 1;

    const toInsert: JSX.Element[] = [element];

    for (const rule of rules) {
      const contextIndex = startingIndex + index;

      const ctx: VisitorContext = {
        index: contextIndex,
        node: element,
        nodeTracker,
      };

      let componentIndex = 0;
      if (rule.match(ctx)) {
        const component = (
          <div
            className='not-prose'
            key={uuidv4()}
          >
            {rule.insertComponent(ctx)}
          </div>
        );
        switch (rule.insertPosition) {
          case 'before':
            componentIndex++;
            toInsert.unshift(component);
            break;
          case 'after':
            toInsert.push(component);
            break;
          case 'replace':
            toInsert.splice(componentIndex, 1, component);
            break;
          default:
            break;
        }
      }
    }

    reactTree.push(...toInsert);
  });
  return reactTree;
};

/**
 * Applies a set of rules to a React tree, allowing for insertion of components
 * at specified positions based on the rules defined.
 *
 * @param {InsertProps} props - The properties for the insertion, including:
 *  - `elements`: The React tree to process
 *  - `rules`: An array of `ParserRule` objects that define how to insert components into the React tree.
 * @returns {React.ReactNode[]} An array of React elements with the rules applied, including any inserted components.
 */
export function applyRulesToReactTree({ elements, rules: unsortedRules }: InserterProps): React.ReactNode[] {
  /* PreProcess the code before searching through the elements */

  if (typeof elements === 'string') {
    throw new Error("React parsing failed - can't insert so falling back to client parser.");
  }

  const elementArray = normalizeToArray(elements);
  const { paywallIndex, paywallElement } = findPaywallIndex(elementArray);

  const nodeTracker: Record<string, number> = {};

  const rules = unsortedRules.sort((a, b) => {
    const priorityA = a.priority ?? EmbedPriority.Medium;
    const priorityB = b.priority ?? EmbedPriority.Medium;
    return priorityB - priorityA; // Higher priority first
  });

  const prePaywallElements = applyRulesHelper({
    elements: elementArray.slice(0, paywallIndex),
    nodeTracker,
    rules,
  });

  const paywalledContentElements = applyRulesHelper({
    elements: paywallElement?.props.children, // JSX.Element implicitly has children as any type
    nodeTracker,
    rules,
    startingIndex: paywallIndex,
  });

  // This is currently empty, but is here in case we end up including data after the paywall
  const postPaywallElements = applyRulesHelper({
    elements: elementArray.slice(paywallIndex + 1),
    nodeTracker,
    rules,
    startingIndex: paywallIndex + paywallElement?.props.children.length,
  });

  const newPaywallNode = paywallElement ? React.cloneElement(paywallElement, {}, paywalledContentElements) : null;

  // Components that should be inserted at the end of the content
  const endCtx: VisitorContext = {
    index: -1, // -1 indicates the end of the array
    node: elementArray[elementArray.length - 1],
    nodeTracker,
  };
  const endRules = rules.filter((rule) => rule.insertPosition === 'end' && rule.match(endCtx));
  const endElements = endRules.map((rule) => {
    return (
      <div
        className='not-prose'
        key={`end-embed-${uuidv4()}`}
      >
        {rule.insertComponent(endCtx)}
      </div>
    );
  });

  return [...prePaywallElements, newPaywallNode, postPaywallElements, ...endElements];
}
